// Recording primitives: mic capture, MediaRecorder, and a screen Wake Lock.

export interface RecordingResult {
  blob: Blob;
  mimeType: string;
  ext: string;
  durationSec: number;
}

// Prefer Opus-in-WebM (Android Chrome, desktop). Fall back to mp4/AAC (iOS Safari).
const MIME_CANDIDATES = [
  { mimeType: "audio/webm;codecs=opus", ext: "webm" },
  { mimeType: "audio/webm", ext: "webm" },
  { mimeType: "audio/mp4", ext: "mp4" },
] as const;

function pickMimeType(): { mimeType: string; ext: string } {
  if (typeof MediaRecorder === "undefined") {
    throw new Error("MediaRecorder is not supported in this browser.");
  }
  for (const cand of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(cand.mimeType)) {
      return { mimeType: cand.mimeType, ext: cand.ext };
    }
  }
  // Let the browser choose (older iOS may report nothing supported).
  return { mimeType: "", ext: "mp4" };
}

const AUDIO_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
  },
};

/** Holds and re-acquires a screen wake lock across visibility changes. */
export class ScreenWakeLock {
  private sentinel: WakeLockSentinel | null = null;
  private active = false;
  private onVisibility = () => {
    if (this.active && document.visibilityState === "visible") {
      void this.acquire();
    }
  };

  get supported(): boolean {
    return "wakeLock" in navigator && !!navigator.wakeLock;
  }

  async start(): Promise<void> {
    this.active = true;
    document.addEventListener("visibilitychange", this.onVisibility);
    await this.acquire();
  }

  async stop(): Promise<void> {
    this.active = false;
    document.removeEventListener("visibilitychange", this.onVisibility);
    if (this.sentinel) {
      try {
        await this.sentinel.release();
      } catch {
        /* ignore */
      }
      this.sentinel = null;
    }
  }

  private async acquire(): Promise<void> {
    if (!this.supported || this.sentinel) return;
    try {
      this.sentinel = await navigator.wakeLock!.request("screen");
      this.sentinel.addEventListener("release", () => {
        this.sentinel = null;
      });
    } catch (err) {
      // Non-fatal: recording continues even if the wake lock is denied.
      console.warn("Wake Lock request failed", err);
    }
  }
}

export class Recorder {
  private stream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;
  private chosen = pickMimeType();
  readonly wakeLock = new ScreenWakeLock();

  get mimeType(): string {
    return this.chosen.mimeType;
  }

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS);
    this.chunks = [];

    const options: MediaRecorderOptions = {};
    if (this.chosen.mimeType) options.mimeType = this.chosen.mimeType;
    this.mediaRecorder = new MediaRecorder(this.stream, options);

    this.mediaRecorder.addEventListener("dataavailable", (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    });

    // Timeslice so we flush data periodically (helps if a tab is killed).
    this.mediaRecorder.start(1000);
    this.startedAt = Date.now();
    await this.wakeLock.start();
  }

  /** Stops recording and resolves with the assembled blob. */
  stop(): Promise<RecordingResult> {
    return new Promise((resolve, reject) => {
      const mr = this.mediaRecorder;
      if (!mr) {
        reject(new Error("Recorder was not started."));
        return;
      }
      mr.addEventListener(
        "stop",
        () => {
          const durationSec = Math.round((Date.now() - this.startedAt) / 1000);
          // Use the recorder's actual mimeType when available.
          const actual = mr.mimeType || this.chosen.mimeType || "audio/mp4";
          const ext = actual.includes("mp4") ? "mp4" : this.chosen.ext;
          const blob = new Blob(this.chunks, { type: actual });
          this.cleanup();
          resolve({ blob, mimeType: actual, ext, durationSec });
        },
        { once: true },
      );
      mr.stop();
      void this.wakeLock.stop();
    });
  }

  private cleanup(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.mediaRecorder = null;
  }
}
